import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import jsPDF from "jspdf";
import { addIdCardCanvasToPdf, captureIdCardElement } from "utils/idCardExport";
import { useTranslation } from "react-i18next";

import { useAuth } from "../../contexts/AuthContext";
import { useSchools } from "../../hooks/useSchools";
import { useSchoolIdForAdmin } from "../../hooks/useSchoolIdForAdmin";
import { useStudents, Student } from "../../hooks/useStudents";
import { useSessions } from "../../hooks/useSession";
import { useClasses } from "../../hooks/useClasses";
import { useSessionClass } from "../../contexts/SessionClassContext";
import { useCurrentStudent } from "../../hooks/useCurrentStudent";

import IdCardTemplate, { IdCardPreviewData } from "components/templates/IdCardTemplate";

interface IdCardFormModalProps {
  open: boolean;
  handleClose: () => void;
  selectedClassId?: number;
  selectedSessionId?: number;
}

const coerceId = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") return Number.isNaN(value) ? null : value;
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
};

const formatDob = (dob: string | number[] | null | undefined) => {
  if (!dob) return "";

  if (Array.isArray(dob) && dob.length >= 3) {
    const [year, month, day] = dob;
    const dd = String(day).padStart(2, "0");
    const mm = String(month).padStart(2, "0");
    const yy = String(year);
    return `${dd}-${mm}-${yy}`;
  }

  if (typeof dob === "string") {
    const ymd = dob.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (ymd) {
      // YYYY-MM-DD -> DD-MM-YYYY
      return `${ymd[3]}-${ymd[2]}-${ymd[1]}`;
    }

    const dmy = dob.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (dmy) return dob;

    // Fallback (leave as-is)
    return dob;
  }

  return "";
};

const sanitize = (s = "") => (s || "").trim().replace(/\s+/g, " ").replace(/[^a-zA-Z0-9-_ ]/g, "");

const buildStudentAddress = (student: Student) => {
  const parts: string[] = [];
  if (student.houseNo) parts.push(`${student.houseNo}`);
  // if (student.street) parts.push(student.street);
  if (student.city) parts.push(student.city);
  // if (student.landmark) parts.push(student.landmark);
  return parts.join(", ");
};

const IdCardFormModal: React.FC<IdCardFormModalProps> = ({
  open,
  handleClose,
  selectedClassId,
  selectedSessionId,
}) => {
  const { t } = useTranslation();
  const { user, isSchoolAdmin } = useAuth();
  const isStudent = user?.roles?.includes("STUDENT") ?? false;

  const { useSchoolById } = useSchools(false, { disableSchoolAdmins: true });
  const shouldFetchAdminSchoolId = !!user && isSchoolAdmin() && !user.schoolId;
  const adminSchoolIdQuery = useSchoolIdForAdmin(shouldFetchAdminSchoolId ? user?.id : undefined);
  const effectiveSchoolId = user?.schoolId ?? adminSchoolIdQuery.data ?? undefined;

  const { data: schoolData } = useSchoolById(effectiveSchoolId);

  const { studentData: currentStudentData, studentLoading: currentStudentLoading } = useCurrentStudent();

  const { selectedSession: contextSelectedSession, selectedClass: contextSelectedClass } = useSessionClass();
  const normalizedSessionId = coerceId(selectedSessionId) ?? coerceId(contextSelectedSession?.id) ?? null;
  const normalizedClassId = coerceId(selectedClassId) ?? coerceId(contextSelectedClass?.id) ?? null;
  const normalizedClassKey =
    contextSelectedClass?.section === "__CLASS_ALL__"
      ? "Class-ALL"
      : contextSelectedClass?.section === "__GRADE_ALL__"
        ? `${String(contextSelectedClass?.name || "").trim()}-ALL`
        : normalizedClassId;

  const { sessionsQuery } = useSessions(effectiveSchoolId);
  const { classesQuery } = useClasses(effectiveSchoolId);

  const { useStudentsBySessionIdAndClassId, useStudentsBySessionIdAndClassName, getStudentByStudentCode } = useStudents(
    false,
    effectiveSchoolId
  );

  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [sessionIdLocal, setSessionIdLocal] = useState<number | null>(normalizedSessionId);
  const [classKeyLocal, setClassKeyLocal] = useState<number | string | null>(normalizedClassKey);

  const [selectedStudentId, setSelectedStudentId] = useState<number | null>(null);

  const [showCard, setShowCard] = useState(false);
  const [idCardData, setIdCardData] = useState<IdCardPreviewData | null>(null);

  const templateRef = useRef<HTMLDivElement | null>(null);
  const [downloading, setDownloading] = useState<null | "image" | "pdf">(null);

  const mapStudentToIdCardData = useCallback(
    (student: Student): IdCardPreviewData => ({
      studentId: student.id,
      schoolName: schoolData?.nameofSchool || "School Name",
      district: schoolData?.district || "",
      schoolTagline: "(Govt. Recognised)",
      schoolAddressLines: [
        "Place your address,",
        `${schoolData?.district || "District"} ${schoolData?.state || "State"} and Pin - ${schoolData?.pinNo || "000000"}`,
      ],
      phoneNo: String(schoolData?.principalMobile || ""),
      studentName: student.studentName || "",
      fatherName: student.fatherName || "",
      motherName: student.motherName || "",
      dob: formatDob(student.dateOfBirth),
      contactNo: student.mobileNo || "",
      mobileNo: student.mobileNo || "",
      address: buildStudentAddress(student),
      className: student.className || "",
      section: (student as any)?.section || "",
    }),
    [schoolData]
  );

  useEffect(() => {
    if (!open) return;

    setError("");
    setIsLoading(false);
    setSessionIdLocal(normalizedSessionId);
    setClassKeyLocal(normalizedClassKey);
    setSelectedStudentId(null);
    setShowCard(false);
    setIdCardData(null);
  }, [open, normalizedSessionId, normalizedClassKey]);

  useEffect(() => {
    if (!open) return;
    if (!isStudent) return;

    if (currentStudentLoading) {
      setIsLoading(true);
      setError("");
      return;
    }

    if (!currentStudentData) {
      setIsLoading(false);
      setError(t("error_loading_student_data") || "Error loading student data");
      return;
    }

    setIdCardData(mapStudentToIdCardData(currentStudentData as Student));
    setShowCard(true);
    setIsLoading(false);
  }, [open, isStudent, currentStudentData, currentStudentLoading, mapStudentToIdCardData, t]);

  const classIdParam = typeof classKeyLocal === "number" ? classKeyLocal : undefined;
  const classNameParam = typeof classKeyLocal === "string" ? classKeyLocal : undefined;

  const filteredStudentsByIdQuery = useStudentsBySessionIdAndClassId(sessionIdLocal ?? undefined, classIdParam);
  const filteredStudentsByNameQuery = useStudentsBySessionIdAndClassName(sessionIdLocal ?? undefined, classNameParam);
  const filteredStudentsQuery = classNameParam ? filteredStudentsByNameQuery : filteredStudentsByIdQuery;
  const sessionOptions = sessionsQuery.data || [];
  const classOptions = classesQuery.data || [];
  const availableStudents = filteredStudentsQuery.data || [];

  const getClassSortValue = (className?: string) => {
    if (!className) return Number.MAX_SAFE_INTEGER;
    const normalized = className.trim().toLowerCase().replace(/[^a-z0-9]/g, "");
    if (normalized.includes("nursery")) return -3;
    if (normalized.includes("lkg")) return -2;
    if (normalized.includes("ukg")) return -1;
    const m = className.match(/\d+/);
    return m ? parseInt(m[0], 10) : Number.MAX_SAFE_INTEGER;
  };

  const getSectionSortValue = (section?: string | null) => {
    if (!section) return 0;
    const s = String(section).trim().toUpperCase();
    if (!s) return 0;
    const code = s.charCodeAt(0);
    return code >= 65 && code <= 90 ? code - 64 : Number.MAX_SAFE_INTEGER;
  };

  const classOptionsWithAll = useMemo(() => {
    const base = Array.isArray(classOptions) ? classOptions : [];
    if (!base.length) return [] as Array<{ key: string; value: number | string; label: string }>;
    const gradeNames = Array.from(new Set(base.map((c: any) => c?.name).filter(Boolean))) as string[];
    gradeNames.sort((a, b) => getClassSortValue(a) - getClassSortValue(b));

    const out: Array<{ key: string; value: number | string; label: string }> = [];
    out.push({ key: "Class-ALL", value: "Class-ALL", label: "Class-ALL" });
    for (const grade of gradeNames) {
      out.push({ key: `${grade}-ALL`, value: `${grade}-ALL`, label: `${grade}-ALL` });
      base
        .filter((c: any) => c?.name === grade)
        .sort((a: any, b: any) => {
          const cd = getClassSortValue(a?.name) - getClassSortValue(b?.name);
          if (cd !== 0) return cd;
          const sd = getSectionSortValue(a?.section) - getSectionSortValue(b?.section);
          if (sd !== 0) return sd;
          return (a?.name || "").localeCompare(b?.name || "");
        })
        .forEach((c: any) => {
          out.push({
            key: String(c.id),
            value: Number(c.id),
            label: c.section ? `${c.name} ${c.section}` : c.name,
          });
        });
    }
    return out;
  }, [classOptions]);

  const sortedStudents = useMemo(() => {
    const list = availableStudents.filter((s: any) => s.status === true);
    return [...list].sort((a: any, b: any) => (a.studentName || "").localeCompare(b.studentName || ""));
  }, [availableStudents]);

  const handleDownloadImage = async () => {
    if (!templateRef.current || !idCardData) return;
    setDownloading("image");
    try {
      const canvas = await captureIdCardElement(templateRef.current);

      const link = document.createElement("a");
      link.download = `id-card-${sanitize(idCardData.studentName)}-${new Date().toISOString().split("T")[0]}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } finally {
      setDownloading(null);
    }
  };

  const handleDownloadPdf = async () => {
    if (!templateRef.current || !idCardData) return;
    setDownloading("pdf");
    try {
      const canvas = await captureIdCardElement(templateRef.current);
      // Use PNG to avoid JPEG compression artifacts that change thin wave lines.
      const imgData = canvas.toDataURL("image/png");

      const pdf = new jsPDF({
        unit: "mm",
        format: "a4",
        orientation: "portrait",
        compress: true,
        putOnlyUsedFonts: true,
      });

      addIdCardCanvasToPdf(pdf, canvas, imgData, "PNG");
      pdf.save(`id-card-${sanitize(idCardData.studentName)}-${new Date().toISOString().split("T")[0]}.pdf`);
    } finally {
      setDownloading(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!sessionIdLocal || !classKeyLocal) {
      setError(t("please_select_session_and_class") || "Please select a session and class");
      return;
    }
    if (!selectedStudentId) {
      setError(t("please_select_student") || t("please_select_a_student") || "Please select a student");
      return;
    }

    const student = sortedStudents.find((s: any) => s.id === selectedStudentId);
    if (!student) {
      setError(t("student_not_found") || "Student not found");
      return;
    }

    setIsLoading(true);
    try {
      let fullStudent = student;
      if ((student as any).studentCode) {
        try {
          fullStudent = await getStudentByStudentCode((student as any).studentCode);
        } catch {
          // Keep fallback student if fetch fails
        }
      }
      setIdCardData(mapStudentToIdCardData(fullStudent as Student));
      setShowCard(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseInternal = () => {
    setError("");
    setIsLoading(false);
    setSelectedStudentId(null);
    setShowCard(false);
    setIdCardData(null);
    handleClose();
  };

  // Student role: show card directly
  if (isStudent && open) {
    return (
      <Dialog
        open={open}
        onClose={handleCloseInternal}
        maxWidth={false}
        fullWidth={false}
        sx={{
          "& .MuiDialog-paper": {
            maxHeight: "90vh",
            width: "fit-content",
            maxWidth: "95vw",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            position: "sticky",
            top: 0,
            zIndex: 1000,
            backgroundColor: "white",
            flexShrink: 0,
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            {t("id_card")}
          </Typography>
          <IconButton onClick={handleCloseInternal} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ overflow: "auto", flex: 1, display: "flex", justifyContent: "center" }}>
          {currentStudentLoading || !schoolData || isLoading ? (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 4 }}>
              <CircularProgress />
              <Typography variant="body2" sx={{ mt: 2 }}>
                {t("loading")}...
              </Typography>
            </Box>
          ) : (
            <Box sx={{ p: 2, width: "fit-content" }}>
              <Box ref={templateRef}>
                {idCardData && <IdCardTemplate data={idCardData} />}
              </Box>

              <Box sx={{ mt: 2, display: "flex", justifyContent: "center", gap: 2, flexWrap: "wrap" }}>
                <Button
                  variant="contained"
                  onClick={handleDownloadImage}
                  disabled={downloading === "image" || !idCardData}
                >
                  {downloading === "image"
                    ? t("downloading") || "Downloading..."
                    : t("download_as_image") || "Download as Image"}
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={handleDownloadPdf}
                  disabled={downloading === "pdf" || !idCardData}
                >
                  {downloading === "pdf"
                    ? t("downloading") || "Downloading..."
                    : t("download_as_pdf") || "Download as PDF"}
                </Button>
              </Box>
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
      </Dialog>
    );
  }

  // Non-student role: after selecting student, show template + download
  if (!isStudent && open && showCard && idCardData) {
    return (
      <Dialog
        open={open}
        onClose={handleCloseInternal}
        maxWidth={false}
        fullWidth={false}
        sx={{
          "& .MuiDialog-paper": {
            maxHeight: "90vh",
            width: "fit-content",
            maxWidth: "95vw",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            position: "sticky",
            top: 0,
            zIndex: 1000,
            backgroundColor: "white",
            flexShrink: 0,
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            {t("id_card")} - {idCardData.studentName}
          </Typography>
          <IconButton onClick={handleCloseInternal} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ overflow: "auto", flex: 1, display: "flex", justifyContent: "center" }}>
          <Box sx={{ p: 2, width: "fit-content" }}>
            <Box ref={templateRef}>
              <IdCardTemplate data={idCardData} />
            </Box>

            <Box sx={{ mt: 2, display: "flex", justifyContent: "center", gap: 2, flexWrap: "wrap" }}>
              <Button
                variant="contained"
                onClick={handleDownloadImage}
                disabled={downloading === "image" || !idCardData}
              >
                {downloading === "image"
                  ? t("downloading") || "Downloading..."
                  : t("download_as_image") || "Download as Image"}
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleDownloadPdf}
                disabled={downloading === "pdf" || !idCardData}
              >
                {downloading === "pdf"
                  ? t("downloading") || "Downloading..."
                  : t("download_as_pdf") || "Download as PDF"}
              </Button>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button variant="outlined" onClick={handleCloseInternal}>
            {t("done") || "Done"}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
          handleCloseInternal();
        }
      }}
      maxWidth="xs"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          maxWidth: "450px",
          width: "95%",
        },
      }}
    >
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant="h5" fontWeight="bold">
          {t("get_id_card") || `Get ${t("id_card")}`}
        </Typography>
        <IconButton onClick={handleCloseInternal} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ mb: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>{t("session") || "Session"}</InputLabel>
              <Select
                label={t("session") || "Session"}
                value={sessionIdLocal ?? ""}
                disabled={sessionsQuery.isLoading}
                onChange={(e) => {
                  setSessionIdLocal(Number(e.target.value));
                  setClassKeyLocal(null);
                  setSelectedStudentId(null);
                  setIdCardData(null);
                  setShowCard(false);
                  setError("");
                }}
              >
                {(sessionOptions || []).map((s: any) => (
                  <MenuItem key={s.id} value={Number(s.id)}>
                    {s.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>{t("class") || "Class"}</InputLabel>
              <Select
                label={t("class") || "Class"}
                value={classKeyLocal ?? ""}
                disabled={classesQuery.isLoading}
                onChange={(e) => {
                  const v = e.target.value as any;
                  if (v === "") {
                    setClassKeyLocal(null);
                  } else if (typeof v === "number") {
                    setClassKeyLocal(v);
                  } else {
                    const asNumber = Number(v);
                    setClassKeyLocal(Number.isFinite(asNumber) && String(v) === String(asNumber) ? asNumber : String(v));
                  }
                  setSelectedStudentId(null);
                  setIdCardData(null);
                  setShowCard(false);
                  setError("");
                }}
              >
                {classOptionsWithAll.map((opt) => (
                  <MenuItem key={opt.key} value={opt.value as any}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {sessionsQuery.isLoading || classesQuery.isLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress size={20} />
              </Box>
            ) : !sessionIdLocal || !classKeyLocal ? (
              <Typography variant="body2" color="text.secondary">
                {t("please_select_session_and_class_from_page") || "Please select session and class from the page."}
              </Typography>
            ) : (
              <>
                <FormControl fullWidth>
                  <InputLabel>{t("student") || "Student"}</InputLabel>
                  <Select
                    label={t("student") || "Student"}
                    value={selectedStudentId ?? ""}
                    onChange={(e) => setSelectedStudentId(Number(e.target.value))}
                    disabled={filteredStudentsQuery.isLoading}
                  >
                    {sortedStudents.length === 0 ? (
                      <MenuItem value="" disabled>
                        {t("no_active_students_found") || "No active students found"}
                      </MenuItem>
                    ) : (
                      sortedStudents.map((s: any) => (
                        <MenuItem key={s.id} value={Number(s.id)}>
                          {s.studentName}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              </>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 2, pb: 2, justifyContent: "space-between" }}>
          <Button variant="outlined" onClick={handleCloseInternal}>
            {t("cancel")}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading || !sessionIdLocal || !classKeyLocal || !selectedStudentId}
            sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" } }}
          >
            {isLoading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                {t("loading") || "Loading..."}
              </>
            ) : (
              t("get_id_card") || "Get ID Card"
            )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default IdCardFormModal;

